import { useRef, type ReactNode, type RefObject } from 'react';
import { ContentAttachments } from '@/components/widget/content-attachments';
import { ProvenanceExpandableAttachments } from './ProvenanceExpandableAttachments';
import { ProvenanceFeed } from './ProvenanceFeed';
import { ProvenanceTranscript } from './ProvenanceTranscript';
import type { AgentHistoryItem } from './provenance-entry-item';
import type { MoveObservationMediaResult } from '@vivport/backend/types';

export interface ProvenanceBodyProps {
  items: AgentHistoryItem[];
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  emptyState?: ReactNode;
  density?: 'normal' | 'compact';
  mode: 'page' | 'pane';
  parentContentId: number;
  attachmentsBreadcrumbLabel?: string;
  onOpenContent?: (contentId: number) => void;
  onObservationMediaMoved?: (result: MoveObservationMediaResult) => void;
  composerSlot?: ReactNode;
  ephemeralSlot?: ReactNode;
  suggestedActionsSlot?: ReactNode;
  composerToolSlot?: ReactNode;
  onRequestCapture?: (args: { mode: 'recording' | 'photo' | 'video'; linkContentId?: number }) => void;
  bottomRef?: RefObject<HTMLDivElement | null>;
}

export function ProvenanceBody({
  items,
  isLoading,
  isError,
  errorMessage,
  emptyState,
  density,
  mode,
  parentContentId,
  attachmentsBreadcrumbLabel,
  onOpenContent,
  onObservationMediaMoved,
  composerSlot,
  ephemeralSlot,
  suggestedActionsSlot,
  composerToolSlot,
  onRequestCapture,
  bottomRef,
}: ProvenanceBodyProps) {
  const fallbackBottomRef = useRef<HTMLDivElement>(null);
  const effectiveBottomRef = (bottomRef ?? fallbackBottomRef) as RefObject<HTMLDivElement>;

  if (mode === 'page') {
    const underlyingDensity = density === 'compact' ? 'compact' : 'comfortable';
    return (
      <>
        <ProvenanceTranscript
          items={items}
          isLoading={isLoading}
          isError={isError}
          errorMessage={errorMessage}
          emptyState={emptyState}
          density={underlyingDensity}
          onOpenContent={onOpenContent}
        />
        <ContentAttachments
          parentContentId={parentContentId}
          title="Attachments"
          emptyText="No attachments yet"
          breadcrumbLabel={attachmentsBreadcrumbLabel ?? 'Attachments'}
        />
      </>
    );
  }

  const underlyingDensity = density === 'normal' ? 'comfortable' : 'compact';
  const feed = isLoading ? (
    <div className="flex-1 overflow-y-auto px-4 pb-3 pt-1.5 text-sm text-muted-foreground">
      Loading…
    </div>
  ) : isError ? (
    <div className="flex-1 overflow-y-auto px-4 pb-3 pt-1.5 text-sm text-destructive">
      {errorMessage ?? 'Failed to load.'}
    </div>
  ) : (
    <ProvenanceFeed
      items={items}
      bottomRef={effectiveBottomRef}
      onRequestCapture={onRequestCapture}
      onOpenContent={onOpenContent}
      onObservationMediaMoved={onObservationMediaMoved}
      density={underlyingDensity}
      ephemeral={ephemeralSlot}
    />
  );

  return (
    <>
      {feed}
      {suggestedActionsSlot}
      {composerToolSlot}
      {composerSlot ? null : (
        <ProvenanceExpandableAttachments parentContentId={parentContentId} />
      )}
      {composerSlot}
    </>
  );
}
